import { computed, inject, reactive } from 'vue';
import { type EventProperties } from '@views/Event/EventTypes';
import { eventKey, saveEventPropertyKey } from '@views/Event/EventEditor/event-editor-keys';

type AddToEvent<T> = (item: T, state: { isLoading: boolean }, eventId: string, ordinal: number, onSuccess: (item: T) => void) => unknown;

/** Shared by the speakers and sponsors pages: the members added to the event, and the event's custom heading for them. */
export function useEventMembers<T extends { Ordinal?: number }>(options: {
    eventId: () => string;
    idOf: (item: T) => string;
    addToEvent: AddToEvent<T>;
    headerProperty: keyof EventProperties;
    headerLabel: string;
}) {
    const event = inject(eventKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;

    const state = reactive({
        added: [] as T[],
        eventIds: [] as string[],
        isLoading: false,
    });

    const customHeader = computed(() => event.value?.EventPropertiesObject?.[options.headerProperty] ?? '');

    function onLoaded(items: T[]) {
        state.eventIds = items.map(options.idOf);
    }

    function onAdded(item: T) {
        state.eventIds.push(options.idOf(item));
        item.Ordinal = state.eventIds.length;
        (state.added as T[]).push(item);
    }

    function onRemoved(id: string) {
        state.eventIds = state.eventIds.filter((eventId) => eventId !== id);
    }

    function onAddToEvent(item: T) {
        options.addToEvent(item, state, options.eventId(), state.eventIds.length + 1, () => onAdded(item));
    }

    function onUpdateCustomHeader(newVal: string) {
        if (customHeader.value === newVal) return;
        const updatedProperties = { ...event.value?.EventPropertiesObject, [options.headerProperty]: newVal };
        saveEventProperty('EventProperties', JSON.stringify(updatedProperties), options.headerLabel);
    }

    return { state, customHeader, onLoaded, onAdded, onRemoved, onAddToEvent, onUpdateCustomHeader };
}
